import { useState, type ReactNode } from 'react';
import { Button, Checkbox, Stack, Text } from '@chakra-ui/react';
import type { NewKeys } from '../data/cloud_secrets.ts';
import { ChromeDialog } from '../ui/chrome_dialog.tsx';

// The emergency key (the recovery key, secrets SE-14), shown once after it was made: a home started,
// its passwords protected, or new keys (docs/specs/cloud-joining.md JN-5). Only it: the home code
// (the space key) shows where it is used, in Settings → Tilez Cloud → Add a device. Kept before going
// on, with a tick box.

/** Why it is shown: a home just started, its passwords just protected, or new keys replacing the old. */
export type SpaceKeyReason = { kind: 'started' } | { kind: 'encrypted'; moved: number } | { kind: 'renewed' };

function why(reason: SpaceKeyReason) : string {
    switch (reason.kind) {
        case 'started':
            return 'Your home on Tilez Cloud is ready.';

        case 'encrypted':
            return 'The passwords your dashboards use are protected now.';

        case 'renewed':
            return 'Your home has new keys. The devices in it keep working; the old emergency key no longer does.';
    }
}

export function EmergencyKeyDialog({ keys, reason, onDone }: { keys: NewKeys; reason: SpaceKeyReason; onDone: () => void }) : ReactNode {
    const [isKept, setIsKept] = useState(false);

    return (
        <ChromeDialog
            title="Your emergency key"
            onClose={() => undefined}
            size="sm"
            focusesField={false}
            footer={
                <Button colorPalette="blue" disabled={!isKept} onClick={onDone}>
                    Done
                </Button>
            }
        >
            <Stack gap="4">
                <Text>{why(reason)}</Text>
                <Text>If you ever lose all your devices, this key gets back the passwords your dashboards use. Write it down, or keep it in a password manager: it is not shown again.</Text>
                <Text fontFamily="mono" fontSize="lg" fontWeight="bold" textAlign="center" userSelect="text" data-testid="recovery-key">
                    {keys.recoveryKey}
                </Text>
                <Text color="fg.muted" fontSize="sm">
                    Without it, and without a device, nobody can get them back: not even Tilez Cloud.
                </Text>
                <Checkbox.Root checked={isKept} onCheckedChange={(change) => setIsKept(change.checked === true)}>
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                    <Checkbox.Label>I have written it down</Checkbox.Label>
                </Checkbox.Root>
            </Stack>
        </ChromeDialog>
    );
}
